import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card,
  CardButton,
  CardContent,
  CardMenu,
  CardMenuItem,
  Cards,
  CenteredPanel,
  ConfirmationDialog,
  Content,
  Heading,
  Inline,
  InlineItem,
  Secondary,
  Stack,
  Text,
  useToast,
  UtilityButton,
} from '@kinesis/bungle';
import { useInventoryId } from '@/hooks/use-inventory-id';
import { useIsEditor } from '@/hooks/use-is-editor';
import { useNow } from '@/hooks/use-now';
import {
  useCreateScenarioMutation,
  useDeleteScenarioMutation,
  useGetScenariosQuery,
} from '@/api/scenarios';
import { Loading } from '@/components/loading';
import { InformationPanel } from '@/components/information-panel';
import { scenarioPath } from '@/links';
import { formatDistanceStrings } from '@/date';
import { ColouredPlanIcon } from './coloured-plan-icon';
import { GridWrapper } from './scenarios.styles';

type DeleteConfirmation = {
  id: number;
  name: string;
};

// The community scenarios list — mirrors suit's `pages/plan`: an
// `InformationPanel` intro, a "Scenarios" heading with the count + an
// `Add scenario` action, and the scenarios as a `Cards` grid (each a `Card`
// linking to the detail page, with a `CardMenu`). Adapted to the community
// model: scenarios carry `name` (not `label`); the menu deletes (village has no
// archive/duplicate) and only non-default scenarios can be deleted.
const Scenarios = () => {
  const navigate = useNavigate();
  const isEditor = useIsEditor();
  const toast = useToast('globalTop');
  const inventoryId = useInventoryId();
  const now = useNow();

  const { data: scenarios, isLoading } = useGetScenariosQuery(inventoryId);
  const count = useMemo(() => scenarios?.length, [scenarios]);

  const [createScenario] = useCreateScenarioMutation();
  const onCreate = useCallback(async () => {
    try {
      const scenario = await createScenario({
        inventoryId,
        name: 'New scenario',
      }).unwrap();
      navigate(scenarioPath(inventoryId, scenario.id));
    } catch {
      toast('Could not create scenario.', { variant: 'error' });
    }
  }, [createScenario, inventoryId, navigate, toast]);

  const [deleteScenario] = useDeleteScenarioMutation();
  const [confirmation, setConfirmation] = useState<DeleteConfirmation>();
  const onDelete = useCallback(
    async (scenarioId: number) => {
      setConfirmation(undefined);
      try {
        await deleteScenario({ scenarioId }).unwrap();
      } catch {
        toast('Could not delete scenario.', { variant: 'error' });
      }
    },
    [deleteScenario, toast],
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <CenteredPanel>
      {confirmation && (
        <ConfirmationDialog
          cancelText='Don’t delete'
          confirmText='Delete scenario'
          onCancel={() => setConfirmation(undefined)}
          onConfirm={() => onDelete(confirmation.id)}
          title={`Delete ${confirmation.name}?`}
          variant='danger'
        >
          Any disclosure pinned to this scenario falls back to the default. This
          cannot be undone.
        </ConfirmationDialog>
      )}
      <Content alignX='center' padding='medium'>
        <GridWrapper>
          <Stack space='medium'>
            <CenteredPanel>
              <Content padding='large' paddingMode='equal'>
                <InformationPanel
                  heading='Build a scenario'
                  renderIcon={<ColouredPlanIcon />}
                >
                  A scenario is the input a disclosure is built from — the
                  inventory scope it covers and the emission factor libraries it
                  uses. Create a scenario to model a different boundary or set
                  of factors.
                </InformationPanel>
              </Content>
            </CenteredPanel>

            <Heading as='h4' size='small'>
              Scenarios
            </Heading>
            <Inline>
              <InlineItem sizing='fill-container'>
                <Secondary>
                  {count} {count === 1 ? 'scenario' : 'scenarios'}
                </Secondary>
              </InlineItem>
              {isEditor && (
                <InlineItem>
                  <UtilityButton
                    icon='plus'
                    magnitude='small'
                    onClick={onCreate}
                  >
                    <Text>Add scenario</Text>
                  </UtilityButton>
                </InlineItem>
              )}
            </Inline>
            <Cards grow view='grid'>
              {scenarios &&
                scenarios.map((scenario) => (
                  <Card key={scenario.id}>
                    <CardButton
                      as={Link}
                      to={scenarioPath(inventoryId, scenario.id)}
                    >
                      <CardContent>
                        <Heading as='h3' size='medium'>
                          {scenario.name}
                        </Heading>
                        <Secondary>
                          {scenario.isDefault
                            ? 'Default scenario'
                            : `Last updated ${formatDistanceStrings(now, scenario.updatedAt)}`}
                        </Secondary>
                      </CardContent>
                    </CardButton>
                    {isEditor && !scenario.isDefault && (
                      <CardMenu>
                        <CardMenuItem
                          variant='danger'
                          onClick={() =>
                            setConfirmation({
                              id: scenario.id,
                              name: scenario.name,
                            })
                          }
                        >
                          Delete
                        </CardMenuItem>
                      </CardMenu>
                    )}
                  </Card>
                ))}
            </Cards>
          </Stack>
        </GridWrapper>
      </Content>
    </CenteredPanel>
  );
};

export { Scenarios };
